import { analytics } from "./index.js";
import { logVirtualCurrencyEarned } from "./appsflyer_events.js";
import { PersistentCache } from "../offline/persistent_cache.js";
import { StatisticsStorage } from "../storage/statistics_storage.js";

const SEEN_KEY = "feature_events_seen_v1";
const MAX_SEEN = 600;

export interface RewardLike
{
  type: string;
  amount: number;
}

let seen: string[] | null = null;

function seenList(): string[]
{
  if ( !seen )
  {
    const stored = PersistentCache.read<string[]>( SEEN_KEY );
    seen = Array.isArray( stored ) ? stored : [];
  }
  return seen;
}

function remember( key: string ): void
{
  const list = seenList();
  list.push( key );
  if ( list.length > MAX_SEEN ) list.splice( 0, list.length - MAX_SEEN );
  PersistentCache.write( SEEN_KEY, list );
}

export function featureHash( text: string ): string
{
  let a = 0x811c9dc5, b = 0x9747b28c;
  for ( let i = 0; i < text.length; i++ )
  {
    const code = text.charCodeAt( i );
    a = Math.imul( a ^ code, 0x01000193 ) >>> 0;
    b = Math.imul( b ^ code, 0x5bd1e995 ) >>> 0;
  }
  return a.toString( 16 ).padStart( 8, "0" ) + b.toString( 16 ).padStart( 8, "0" );
}

export function featureDateTime( iso: string | null | undefined ): string
{
  if ( !iso ) return "";
  const date = new Date( iso );
  return Number.isNaN( date.getTime() ) ? "" : date.toISOString().slice( 0, 19 ).replace( "T", " " );
}

export function balanceParams(): Record<string, unknown>
{
  const stats = StatisticsStorage.load();
  return {
    balance_coins: stats.coins,
    balance_hearts: stats.hearts,
    balance_big_balls: stats.big_balls,
    balance_infinite_balls: stats.infinite_balls,
    balance_multi_balls: stats.multiballs,
    balance_fireworks: stats.fireworks,
  };
}

/** "coin:100|fireworks:2" — one string column for a reward bundle, the same shape the server uses for mystery_box_roll. */
export function rewardsLabel( rewards: RewardLike[] | Record<string, unknown> | null | undefined ): string
{
  if ( !rewards ) return "";
  const entries: RewardLike[] = Array.isArray( rewards )
    ? rewards
    : Object.entries( rewards )
      .filter( ( [ , value ] ) => typeof value === "number" && value > 0 )
      .map( ( [ type, amount ] ) => ( { type, amount: amount as number } ) );
  return entries.filter( reward => reward.amount > 0 ).map( reward => `${reward.type}:${reward.amount}` ).join( "|" );
}

function coinsOf( rewards: RewardLike[] | Record<string, unknown> | null | undefined ): number
{
  if ( !rewards ) return 0;
  if ( Array.isArray( rewards ) ) return rewards.filter( reward => reward.type === "coin" || reward.type === "coins" ).reduce( ( sum, reward ) => sum + reward.amount, 0 );
  const value = ( rewards as Record<string, unknown> ).coins;
  return typeof value === "number" && value > 0 ? value : 0;
}

/** AppsFlyer virtual_currency_earned for the coins in a reward bundle. */
export function logRewardCoins( rewards: RewardLike[] | Record<string, unknown> | null | undefined, source: string ): void
{
  const amount = coinsOf( rewards );
  if ( amount > 0 ) void logVirtualCurrencyEarned( { currency_name: "coins", amount, source } );
}

/**
 * One analytics event of a game feature (daily tasks, tower, tournaments). With
 * a dedupe key the event is sent once per key on this device, so re-observing
 * the same server state never repeats it; req_trans_id is derived from the
 * same key so downstream can drop repeats from other devices too.
 */
export function trackFeatureEvent( name: string, properties: Record<string, unknown>, dedupe_key?: string ): boolean
{
  const key = dedupe_key ? `${name}|${dedupe_key}` : null;
  if ( key && seenList().includes( key ) ) return false;
  analytics.track( name, {
    sourceId: name,
    req_trans_id: featureHash( key ?? `${name}|${Date.now()}|${Math.random()}` ),
    ...properties,
    ...balanceParams(),
  } );
  if ( key ) remember( key );
  return true;
}
